import { ref, type Ref } from "vue";
import { v4 as uuid } from "uuid";
import { useQueryClient } from "@tanstack/vue-query";
import { QueryKey } from "@/stores/queries/query-keys";
import {
  useCreateFieldObservation,
  useGetFieldParts,
} from "@/stores/queries/field-observations.query";
import { readLastSelectedFieldPart } from "@/utils/field-parts.utils";
import { trackTab } from "@/analytics/drawing-analytics";
import type { FieldObservation } from "@/types/field-observations.types";

/**
 * Création d'une observation depuis le volet canvas : l'observation est créée
 * tout de suite (désordre par défaut, `localId` systématique — c'est la clé
 * du rattachement plan ↔ observation), les éléments fournis lui sont
 * rattachés d'office, puis sa carte s'ouvre dans la liste pour la compléter.
 *
 * Partie par défaut : celle imposée par l'appelant (« + » sur une partie de
 * l'arborescence), sinon la dernière partie utilisée sur cet ouvrage, si
 * elle existe toujours.
 */
export function useCreatePlanObservation(options: {
  diagnosticId: Ref<number>;
  stampHandles: (handles: string[], observationLocalId: string) => number;
  onCreated: (observation: FieldObservation) => void;
}): {
  isCreating: Ref<boolean>;
  createObservation: (params?: {
    handles?: string[];
    partId?: number;
  }) => Promise<FieldObservation | null>;
} {
  const { diagnosticId } = options;
  const queryClient = useQueryClient();
  const { mutateAsync } = useCreateFieldObservation();
  const { data: parts } = useGetFieldParts(diagnosticId);
  const isCreating = ref(false);

  /** La partie mémorisée n'est proposée que si elle existe encore. */
  const rememberedPartId = (): number | undefined => {
    const remembered = readLastSelectedFieldPart(diagnosticId.value);
    if (!remembered) return undefined;
    if (parts.value && !parts.value.some((part) => part.id === remembered)) {
      return undefined;
    }
    return remembered;
  };

  const createObservation = async (
    params: { handles?: string[]; partId?: number } = {},
  ): Promise<FieldObservation | null> => {
    if (isCreating.value || !diagnosticId.value) return null;
    isCreating.value = true;
    try {
      const partId = params.partId ?? rememberedPartId();
      const observation = await mutateAsync({
        type: "DISORDER",
        diagnosticId: diagnosticId.value,
        ...(partId ? { partId } : {}),
        localId: uuid(),
      });
      trackTab("observations", "observation_created");

      // La carte doit pouvoir s'ouvrir sans attendre le rechargement de la
      // liste : on y glisse l'observation.
      const listKey = [
        QueryKey.FIELD_OBSERVATIONS,
        { diagnosticId: observation.diagnosticId },
      ];
      queryClient.setQueryData<FieldObservation[]>(listKey, (current) =>
        current
          ? [...current.filter((o) => o.id !== observation.id), observation]
          : [observation],
      );
      // Puis on relit la liste. L'invalidation faite par la mutation ne suffit
      // pas quand le PREMIER chargement est encore en vol (création depuis le
      // menu de sélection, volet fermé) : vue-query garde alors la requête en
      // cours, partie avant la création, et sa réponse effacerait
      // l'observation de la liste. Maintenant que la liste a un contenu, cette
      // invalidation annule la requête en vol et en relance une à jour.
      void queryClient.invalidateQueries({ queryKey: listKey });

      // Rattachement d'office des éléments fournis (sélection, marqueur).
      if (observation.localId && params.handles?.length) {
        options.stampHandles(params.handles, observation.localId);
      }
      options.onCreated(observation);
      return observation;
    } catch (error) {
      // La mutation a déjà notifié l'échec ; rien ne s'ouvre.
      console.error("Failed to create observation:", error);
      return null;
    } finally {
      isCreating.value = false;
    }
  };

  return { isCreating, createObservation };
}
